import { useRef } from 'react';
import i18next from 'i18next';
import { hideNotification, showNotification, updateNotification } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { Group, UnstyledButton } from '@mantine/core';
import { handleError } from '@/helpers';
import { NOTIFICATION_AUTO_CLOSE_TIME } from '@/constants';

type NotificationAction = {
  label: string;
  onClick: () => void;
};

type NotificationConfig = {
  title?: string;
  message?: string | null;
  namespace?: string;
  action?: string;
  actionButton?: NotificationAction;
  autoClose?: number | false;
};

type ErrorNotificationConfig = {
  titleKey?: string;
  messageKey?: string;
  namespace?: string;
  color?: string;
  autoClose?: boolean;
  action?: string;
};

export const useNotification = () => {
  const notificationId = useRef('');

  const showLoadingNotification = ({
    title = 'notifications.loadingTitle',
    message = 'notifications.loadingMessage',
    namespace = 'global',
    action = '',
  }: NotificationConfig) => {
    notificationId.current = showNotification({
      loading: true,
      title: i18next.t(title, { ns: namespace }),
      message: message ? i18next.t(message, { ns: namespace, action }) : null,
      autoClose: false,
      withCloseButton: false,
    });
  };

  const showSuccessNotification = ({
    title = 'notifications.successTitle',
    message = 'notifications.successMessage',
    namespace = 'global',
    action = 'action',
    actionButton,
    autoClose,
  }: NotificationConfig) => {
    updateNotification({
      id: notificationId.current,
      color: 'teal',
      title: actionButton ? (
        <Group justify="space-between" gap="md">
          {i18next.t(title, { ns: namespace })}
          <UnstyledButton
            c="brand.6"
            fz="sm"
            onClick={() => {
              actionButton?.onClick();
              hideNotification(notificationId.current);
            }}
          >
            {actionButton?.label}
          </UnstyledButton>
        </Group>
      ) : (
        i18next.t(title, { ns: namespace })
      ),
      message: message ? i18next.t(message, { ns: namespace, action }) : null,
      icon: <IconCheck size={18} />,
      loading: false,
      autoClose: autoClose ?? (actionButton ? false : 2000),
      withCloseButton: true,
      ...(actionButton && { action: actionButton }),
    });
  };

  const showErrorNotification = (error: unknown, config?: ErrorNotificationConfig) => {
    if (config) {
      const namespace = config.namespace || 'global';
      const title = i18next.t(config.titleKey || 'notifications.errorTitle', { ns: namespace });
      const message = i18next.t(config.messageKey || 'notifications.errorMessage', {
        ns: namespace,
        action: config.action,
      });
      const color = config.color || 'red';
      const autoClose = config.autoClose ?? NOTIFICATION_AUTO_CLOSE_TIME;

      if (notificationId.current) {
        updateNotification({
          id: notificationId.current,
          title,
          message,
          color,
          loading: false,
          autoClose,
          withCloseButton: true,
        });
        notificationId.current = '';
        return;
      }

      showNotification({
        title,
        message,
        color,
        autoClose,
        withCloseButton: true,
      });
      return;
    }

    if (notificationId.current) {
      try {
        handleError(error, notificationId.current);
      } catch (_) {
        hideNotification(notificationId.current);
        handleError(error);
      } finally {
        notificationId.current = '';
      }
      return;
    }

    handleError(error);
  };

  const hideActiveNotification = () => {
    if (!notificationId.current) {
      return;
    }

    hideNotification(notificationId.current);
    notificationId.current = '';
  };

  return {
    showLoadingNotification,
    showSuccessNotification,
    showErrorNotification,
    hideActiveNotification,
  };
};
